import React from "react";

import { ArrowBack, Done, Upload, ZoomIn, ZoomOut } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import Rotate90DegreesCcwIcon from "@mui/icons-material/Rotate90DegreesCcw";
import { IconButton, styled, Stack, Box, Button, Typography } from "@mui/material";

import axios from "axios";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import Slider from "react-slick";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import "./Gallery.css";

const CustomizedSlider = styled(Slider)`
  .slick-slider,
  .slick-list {
    max-width: 100vw;
    width: 100%;
    overflow: hidden !important;
    padding: 0 3.2rem;
  }

  .slick-slide {
    margin: 0 8px;
  }

  .slick-prev,
  .slick-next {
    top: 45%;
    position: absolute;
  }

  .slick-prev {
    left: 1.8rem;
  }
  .slick-next {
    right: 1.8rem;
  }
  .slick-disabled {
    opacity: 0;
    visibility: hidden;
  }

  .slick-current > div > div {
    border: 2px solid #f7931e;
  }
`;

function Arrow(props) {
  const { className, onClick, direction } = props;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="7.2rem"
      height="7.2rem"
      boxShadow="0px 4px 12px rgba(33, 33, 33, 0.15)"
      borderRadius="10rem"
      bgcolor="#fff"
      className={className}
      onClick={onClick}
      right={direction === "next" ? "1rem" : "unset"}
      left={direction === "next" ? "unset" : "2rem"}
      top="10rem"
      sx={{
        zIndex: 1,

        "&:hover": {
          bgcolor: "#fff"
        },

        "&::before": {
          display: "none"
        },
        ".slick-disabled": {
          display: "block"
        }
      }}
    >
      <IconButton sx={{ p: 0 }}>
        <ArrowBack
          htmlColor="#000"
          sx={{
            transform: direction === "next" ? "rotate(180deg)" : "rotate(0)"
          }}
        />
      </IconButton>
    </Box>
  );
}

function ImageControls({ handle, zoomIn, zoomOut }) {
  return (
    <Stack
      direction="row"
      minWidth="16.6rem"
      height="3.2rem"
      spacing={1}
      justifyContent="space-between"
      px=".5rem"
      sx={{
        backgroundColor: "#3D3D3D",
        borderRadius: "4px"
      }}
    >
      <IconButton
        onClick={() => zoomOut()}
        sx={{
          padding: 0
        }}
      >
        <ZoomOut htmlColor="#fff" />
      </IconButton>

      <Button
        onClick={handle.active ? handle.exit : handle.enter}
        sx={{
          padding: "0 .5rem",
          color: "#fff",
          fontSize: ["1.4rem"],
          fontWeight: 400
        }}
      >
        {handle.active ? "Exit Full Screen" : "Full Screen"}
      </Button>

      <IconButton
        onClick={() => zoomIn()}
        sx={{
          padding: 0
        }}
      >
        <ZoomIn htmlColor="#fff" />
      </IconButton>
    </Stack>
  );
}

function DownloadImage({ currImage, currImageName }) {
  const handleDownloadImage = async () => {
    fetch(currImage)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        const imageName = currImageName ? currImageName.split(".")[0] : "image";
        a.download = `${imageName}.jpg`;
        a.href = blobUrl;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((err) => console.error("error downloading image", err));
  };

  return (
    <Stack
      direction="row"
      minWidth="16.6rem"
      height="3.2rem"
      spacing={1}
      justifyContent="space-between"
      px=".5rem"
      sx={{
        backgroundColor: "#3D3D3D",
        borderRadius: "4px"
      }}
    >
      <IconButton
        sx={{
          padding: 0
        }}
      >
        <DownloadIcon htmlColor="#fff" />
      </IconButton>

      <Button
        sx={{
          padding: "0 .5rem",
          color: "#fff",
          fontSize: ["1.4rem"],
          fontWeight: 400
        }}
        onClick={handleDownloadImage}
      >
        Download image
      </Button>
    </Stack>
  );
}

function RotateImage({ rotateToLeft, rotateToRight }) {
  return (
    <Stack
      direction="row"
      minWidth="16.6rem"
      height="3.2rem"
      spacing={1}
      justifyContent="space-between"
      px=".5rem"
      sx={{
        backgroundColor: "#3D3D3D",
        borderRadius: "4px"
      }}
    >
      <IconButton
        sx={{
          padding: 0,
          transform: "scaleX(-1)"
        }}
        onClick={rotateToLeft}
      >
        <Rotate90DegreesCcwIcon htmlColor="#fff" />
      </IconButton>

      <Button
        sx={{
          padding: "0 .5rem",
          color: "#fff",
          fontSize: ["1.4rem"],
          fontWeight: 400
        }}
      >
        Rotate
      </Button>

      <IconButton
        sx={{
          padding: 0
        }}
        onClick={rotateToRight}
      >
        <Rotate90DegreesCcwIcon htmlColor="#fff" />
      </IconButton>
    </Stack>
  );
}

function ActiveIcon() {
  return (
    <Box
      width="2.4rem"
      height="2.4rem"
      borderRadius="50%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        position: "absolute",
        bottom: "1rem",
        right: "1rem",
        bgcolor: "#F7931E"
      }}
    >
      <Done fontSize=".8rem" htmlColor="#fff" />
    </Box>
  );
}

function Gallery({ onClose, images, hideDownloadImage }) {
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [rotationDegree, setRotationDegree] = React.useState(0);
  const handle = useFullScreenHandle();

  const rotateToLeft = () => {
    setRotationDegree((prev) => prev + 90);
  };

  const rotateToRight = () => {
    setRotationDegree((prev) => prev - 90);
  };

  return (
    <Stack bgcolor="#6D6D6D" sx={{ height: "100vh", overflow: "scroll" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack>
          {images[activeSlideIndex]?.name && (
            <Typography m="1.8rem 2.4rem" color="#fff" fontWeight={600}>
              {images[activeSlideIndex]?.name}
            </Typography>
          )}
        </Stack>
        <IconButton sx={{ marginRight: "1.8rem", padding: "9px" }} onClick={onClose}>
          <CloseIcon htmlColor="#fff" />
        </IconButton>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        position="relative"
        height="50%"
        margin="10rem 0"
        sx={{
          backgroundColor: "#6D6D6D",
          "& > .fullscreen": {
            width: "100%",
            height: "100%"
          }
        }}
      >
        <TransformWrapper initialScale={1}>
          {({ zoomIn, zoomOut }) => (
            <FullScreen handle={handle}>
              <TransformComponent
                wrapperStyle={{
                  height: "100%",
                  margin: "0 auto",
                  transform: `rotate(${rotationDegree}deg)`,
                  transition: "300ms linear all"
                }}
                contentStyle={{
                  height: "100%"
                }}
              >
                <Box
                  component="img"
                  src={images[activeSlideIndex]?.url || images[activeSlideIndex]?.path}
                  height="100%"
                  margin="0 auto"
                  sx={{
                    objectFit: "contain"
                  }}
                />
              </TransformComponent>
              <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" marginTop="3rem">
                <RotateImage rotateToLeft={rotateToLeft} rotateToRight={rotateToRight} />
                <ImageControls zoomIn={zoomIn} zoomOut={zoomOut} handle={handle} />
                {!hideDownloadImage && (
                  <DownloadImage
                    currImage={images[activeSlideIndex]?.url || images[activeSlideIndex]?.path}
                    currImageName={images[activeSlideIndex]?.name}
                  />
                )}
              </Stack>
            </FullScreen>
          )}
        </TransformWrapper>
      </Stack>
      <CustomizedSlider
        swipeToSlide
        slidesToScroll={1}
        variableWidth
        infinite={false}
        focusOnSelect
        nextArrow={<Arrow direction="next" />}
        prevArrow={<Arrow />}
        beforeChange={(current, next) => {
          setActiveSlideIndex(next);
        }}
      >
        {images.map((image, id) => (
          <Stack
            key={image.url || image.path}
            position="relative"
            direction="row"
            alignItems="center"
            justifyContent="center"
          >
            <Box
              component="img"
              src={image.url || image.path}
              alt="as"
              width="20rem"
              height="20rem"
              sx={{
                objectFit: "contain"
              }}
            />
            {activeSlideIndex === id && <ActiveIcon />}
          </Stack>
        ))}
      </CustomizedSlider>
    </Stack>
  );
}

export default Gallery;
